/**
 * Layout primitives for the W-7 replica.
 *
 * The official form is a fixed-layout page, so the replica is too: every
 * element is placed at the coordinate measured out of the PDF rather than
 * being flowed and nudged. `pt()` converts PDF points to px through the
 * --w7px custom property set on .w7-page, so one number controls the scale.
 *
 * See w7-rules.ts for the measured rules and w7-geometry.ts for the width.
 *
 * ---------------------------------------------------------------------------
 * Text is placed by BASELINE
 * ---------------------------------------------------------------------------
 * `At` takes `base`, the run's baseline in points from the page top — the same
 * quantity the PDF itself stores — and derives `top` from the browser's own
 * measured font metrics. It is deliberately NOT named `y`: this file used to
 * take a glyph-box top and feed it straight to CSS `top`, which is a different
 * quantity, and the page absorbed the difference in a global `line-height: 1.24`
 * fudge. The error that leaves is proportional to type size, so one factor
 * cannot serve 7, 8, 9, 10, 14 and 24pt at once — it left the body copy 1.2pt
 * high and "Sign Here" 2.7pt high, which is what read as text crowding the rule
 * above it. Renaming the prop makes any un-migrated call site a compile error
 * instead of a silent 1-3pt shift.
 *
 * See src/utils/formFontMetrics.ts for how the metrics are measured.
 */
import { CSSProperties, ReactNode, useEffect, useState } from "react";
import {
  FaceSpec,
  baselineOffset,
  faceMetrics,
  isMeasured,
  loadFormFontMetrics,
} from "../../../utils/formFontMetrics";
import { CHECKBOXES, CHECKBOX_PT, H_RULES, V_RULES } from "./w7-rules";

/** PDF points -> css length at the page's current scale. */
export const pt = (n: number) => `calc(${n} * var(--w7px))`;

/** The official's hairline weight, in points. */
export const RULE_PT = 0.5;

/**
 * A rule's thickness as a css length, scaled through pt() like everything else.
 *
 * This file used to write the weight as a bare `0.5pt`, which the browser
 * resolves against the CSS reference pixel (96/72 = 0.667px) rather than the
 * page's own scale (0.5 * 2.9413 = 1.47px) — so every rule came out 2.2x thin.
 */
export const RULE_LEN = pt(RULE_PT);

/**
 * Rules are drawn as FILLED BOXES (width/height + background), not as borders.
 *
 * Chrome snaps border-width to whole DEVICE pixels, so a border can never carry
 * a fractional weight: measured through html2canvas at scale 2, a 0.5pt rule
 * rasterises to 2px on a 1x display and only 1px on a 2x one, against the 2.94px
 * the official actually sets. Element geometry is not snapped that way, so a
 * filled box lands on 2.88-3.00px on both — which is the whole point, since the
 * thin grey rules were what made the capture read as washed out.
 *
 * Boxes that need all four sides (the checkbox, the 6f digit cells) still use a
 * scaled border: snapping costs them a little weight but restructuring them
 * would not be worth the risk.
 */
export const RULE_W = `${RULE_LEN} solid #000`;

/* ==========================================================================
   The faces the official sets, from a census of its content stream.

   Page 1 uses, by run count: 8pt Roman (94), 8pt Bd (36), 7pt Roman (30),
   6pt pi marks (12+2), 10pt Bd (9), 9pt Roman (9), 9pt Bd (8), 14pt Franklin
   Demi (4), 10pt Franklin Demi (3), 9pt BdIt (2), 5pt Roman (2), 24pt BlkCn (1),
   9pt It (1), 50pt Times (1, the brace — drawn, not typeset).

   Note the ten 10pt left-gutter row heads are HelveticaNeue Bd, NOT the title
   face; only "Acceptance / Agent's / Use ONLY" (10pt) and "Sign / Here" (14pt)
   are Franklin Demi. Those five were previously set bold in the body face.
   ========================================================================== */
/**
 * Half a rule's weight, in points. The measured y of a rule is where the
 * official draws the STROKE, so a rule whose top edge sits on that number ends
 * up half its weight too low — measured at 300dpi, our rule centres were
 * 0.24pt below the official's. Offsetting by half the weight centres the
 * stroke on the measurement instead.
 */
const RULE_HALF = RULE_PT / 2;

const FONT_BODY =
  '"HelveticaNeueLTStd", "Helvetica Neue", Helvetica, Arial, sans-serif';
/** The wordmark face — a separate condensed design, not a heavier body weight. */
export const FONT_WORDMARK =
  '"HelveticaNeueLTStd-BlkCn", "Helvetica Neue", Arial, sans-serif';
/** The title face. */
export const FONT_TITLE =
  '"ITCFranklinGothicStd-Demi", "Helvetica Neue", Arial, sans-serif';

export const W7_FACES = {
  roman: { family: FONT_BODY, weight: 400, style: "normal" },
  bd: { family: FONT_BODY, weight: 700, style: "normal" },
  it: { family: FONT_BODY, weight: 400, style: "italic" },
  bdit: { family: FONT_BODY, weight: 700, style: "italic" },
  blkcn: { family: FONT_WORDMARK, weight: 400, style: "normal" },
  demi: { family: FONT_TITLE, weight: 400, style: "normal" },
} as const satisfies Record<string, FaceSpec>;

export type FaceKey = keyof typeof W7_FACES;

export const W7_FACE_LIST: readonly FaceSpec[] = Object.values(W7_FACES);

/**
 * Default line pitch, as a multiple of type size.
 *
 * Any value at or above the tallest face's content box works, because `At`
 * subtracts the resulting half-leading back out — this only has to be big
 * enough that no glyph is the thing setting the line box height. 1.25 clears
 * the widest reading of these fonts (OS/2 win metrics put HelveticaNeue Bd at
 * 1.193em).
 */
const LINE_FACTOR = 1.25;

/**
 * Measures the page's faces, then re-renders so every `At` picks up the real
 * numbers. Call once, high in the page.
 *
 * Until it resolves, `faceMetrics` returns each font's hhea metrics, which is
 * what Blink resolves on macOS anyway — so first paint is already correct there
 * and merely close elsewhere.
 */
export const useW7FontMetrics = (): boolean => {
  const [ready, setReady] = useState(() => W7_FACE_LIST.every(isMeasured));
  useEffect(() => {
    if (ready) return;
    let alive = true;
    loadFormFontMetrics(W7_FACE_LIST).then(() => {
      if (alive) setReady(true);
    });
    return () => {
      alive = false;
    };
  }, [ready]);
  return ready;
};

type AtProps = {
  x: number;
  /** The run's BASELINE, in points from the page top. Not its box top. */
  base: number;
  /** Column width in points. Only needed for `align`, or to bound a run. */
  w?: number;
  /** points; defaults to the 8pt body */
  size?: number;
  bold?: boolean;
  italic?: boolean;
  /** Overrides the family. Use for the Franklin Demi and BlkCn runs. */
  face?: FaceKey;
  /**
   * Line pitch in points, for the two multi-line blocks. The official sets
   * both at 1.20em (7pt perjury block: baselines 8.40pt apart; 9pt reason
   * block: 10.80pt), so this is measured, not chosen.
   */
  leading?: number;
  /**
   * The official's horizontal scaling (Tz) for this run, 0-1.
   *
   * Eleven runs on page 1 are genuinely condensed in the content stream — the
   * masthead's Treasury lines at 0.95, "Date of birth (month / day / year)" at
   * 0.90, and so on. This reproduces that, and ONLY that: with the real
   * HelveticaNeueLTStd loaded, every other run's advance widths match the
   * official's to 0.01pt, so anything else scaled here would be scaling a run
   * that already fits.
   */
  tz?: number;
  align?: CSSProperties["textAlign"];
  nowrap?: boolean;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
};

/**
 * Places a run so its baseline lands exactly on `base`.
 *
 * A run that mixes faces inline (the reason paragraph switches Roman/Bd
 * mid-sentence) takes its baseline from the block's own strut, i.e. from
 * `face`/`bold`/`italic` here. Where the mixed faces disagree about ascent the
 * line box is set by the tallest — worth at most 0.08pt at 8pt, and exactly
 * zero wherever the engine reads hhea, since all four HelveticaNeue cuts share
 * it. Not worth an API for.
 */
export const At = ({
  x,
  base,
  w,
  size = 8,
  bold,
  italic,
  face,
  leading,
  tz,
  align,
  nowrap = true,
  className,
  style,
  children,
}: AtProps) => {
  const faceKey: FaceKey =
    face ?? (bold ? (italic ? "bdit" : "bd") : italic ? "it" : "roman");
  const spec = W7_FACES[faceKey];
  const lead = leading ?? size * LINE_FACTOR;
  const top = base - baselineOffset(faceMetrics(spec), size, lead);

  return (
    <div
      className={className}
      style={{
        position: "absolute",
        left: pt(x),
        top: pt(top),
        // Tz scales from the current point, so the box is widened by 1/tz to
        // leave the run occupying exactly `w` once the transform has run.
        width: w != null ? pt(tz ? w / tz : w) : undefined,
        fontFamily: spec.family,
        fontSize: pt(size),
        // Explicit, in points: this is the term baselineOffset() just cancelled
        // out, so the two have to agree.
        lineHeight: pt(lead),
        fontWeight: spec.weight,
        fontStyle: spec.style,
        textAlign: align,
        whiteSpace: nowrap ? "nowrap" : "normal",
        transform: tz ? `scaleX(${tz})` : undefined,
        transformOrigin: tz ? "left top" : undefined,
        ...style,
      }}
    >
      {children}
    </div>
  );
};

/** The 8x8pt checkbox the form uses everywhere. All 21 verified against the PDF. */
export const Cb = ({
  x,
  y,
  checked,
  onChange,
  disabled,
}: {
  x: number;
  y: number;
  checked?: boolean;
  onChange?: () => void;
  disabled?: boolean;
}) => (
  <div
    onClick={disabled ? undefined : onChange}
    style={{
      position: "absolute",
      left: pt(x),
      top: pt(y),
      width: pt(CHECKBOX_PT),
      height: pt(CHECKBOX_PT),
      border: RULE_W,
      background: "#fff",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: pt(7),
      lineHeight: 1,
      cursor: disabled ? "default" : "pointer",
      userSelect: "none",
    }}
  >
    {checked ? "✔" : ""}
  </div>
);

/**
 * The small "go to" arrowhead, drawn to its measured ink box.
 *
 * These were previously a "▶" character in the inherited body font. The
 * official sets them in UniversalStd-NewswithCommPi, and the copy of that face
 * bundled here is a seven-glyph subset — bullet, squares, a disc — with no
 * triangle in it at all, so every one of the twelve marks on page 1 was
 * silently falling back to a system symbol font at whatever size and shape that
 * font happened to use. Drawn instead, from the ink measured off a 2400dpi
 * render: six Universal marks at 5.83 x 5.31pt, ink starting 0.10pt past the
 * glyph origin with its top 4.52pt above the baseline.
 *
 * Fractions of `size` rather than absolutes, so the geometry survives the two
 * marks the official sets at a different size.
 */
export const Arrow = ({
  x,
  base,
  size = 6,
}: {
  x: number;
  base: number;
  size?: number;
}) => (
  <div
    style={{
      position: "absolute",
      left: pt(x + 0.0167 * size),
      top: pt(base - 0.7533 * size),
      width: 0,
      height: 0,
      borderTop: `${pt(0.4425 * size)} solid transparent`,
      borderBottom: `${pt(0.4425 * size)} solid transparent`,
      borderLeft: `${pt(0.9717 * size)} solid #000`,
    }}
  />
);

/**
 * The two arrowheads the official sets in KozGoProVI-Medium instead — beside
 * "Name of college/university" and "Length of stay".
 *
 * A different design from `Arrow`, not a different size of it: measured at
 * 3.87 x 5.68pt against the Universal mark's 5.83 x 5.31, i.e. narrow and tall
 * where the other is wide and squat.
 */
export const ArrowKoz = ({
  x,
  base,
  size = 6,
}: {
  x: number;
  base: number;
  size?: number;
}) => (
  <div
    style={{
      position: "absolute",
      left: pt(x + 0.245 * size),
      top: pt(base - 0.8517 * size),
      width: 0,
      height: 0,
      borderTop: `${pt(0.4733 * size)} solid transparent`,
      borderBottom: `${pt(0.4733 * size)} solid transparent`,
      borderLeft: `${pt(0.645 * size)} solid #000`,
    }}
  />
);

/**
 * The tall arrowhead beside each signature line, drawn to its measured ink box.
 *
 * The official sets these as rotated AdobePiStd glyphs, which pdfplumber decodes
 * as "▲" — a bad Unicode guess, since the ink is far taller than it is wide.
 * Measured at 2400dpi across all five: the ink is 0.262 x 0.907 of the glyph
 * size, offset (0.0747, 0.0448) from the glyph origin. Two sizes appear, 24.08pt
 * beside the applicant signatures and 21.41pt elsewhere.
 *
 * `x`/`y` are the glyph's origin, so they can be copied straight from the
 * extraction; the ink offsets are applied here.
 */
export const Marker = ({
  x,
  y,
  size,
}: {
  x: number;
  y: number;
  size: number;
}) => (
  <div
    style={{
      position: "absolute",
      left: pt(x + 0.0747 * size),
      top: pt(y + 0.0448 * size),
      width: 0,
      height: 0,
      borderTop: `${pt(0.4536 * size)} solid transparent`,
      borderBottom: `${pt(0.4536 * size)} solid transparent`,
      borderLeft: `${pt(0.262 * size)} solid #000`,
    }}
  />
);

/*
 * The brace joining reasons d and e to their follow-up questions.
 *
 * Drawn rather than typeset. The official's is a 50pt Times "}" set at Tz 0.50,
 * whose ink box measures 5.9 x 42.5pt at x 212, y 185 — proportions no font
 * glyph reproduces at a predictable weight, and a glyph would also shift with
 * whatever face the renderer fell back to.
 *
 * Path units are hundredths of a point, so the numbers are the measured stroke
 * centres relative to the box's left edge at x=212: left tips at 212.85, stem at
 * 215.12, nib at 216.95. Stroke 1.9pt, which puts the nib tip on the official's
 * at 217.9.
 *
 * width/height are declared on the root so the SVG has an intrinsic size —
 * without them html2canvas cannot size the image and drops it.
 */
const BRACE_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="59" height="425" ' +
  'viewBox="0 0 590 4250" preserveAspectRatio="none">' +
  '<path d="M85 95 C300 95 312 210 312 470 L312 1900 ' +
  "C312 2060 340 2125 495 2125 C340 2125 312 2190 312 2350 " +
  'L312 3780 C312 4040 300 4155 85 4155" ' +
  'fill="none" stroke="#000" stroke-width="190" ' +
  'stroke-linecap="round" stroke-linejoin="round"/></svg>';

const BRACE_URL = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
  BRACE_SVG,
)}`;

/**
 * It is an <img> rather than a background-image because html2canvas draws img
 * elements directly but has no gradient or SVG-background support — as a
 * background it renders in the browser and then vanishes from the capture.
 */
export const Brace = ({
  x,
  y,
  w = 5.9,
  h = 42.5,
}: {
  x: number;
  y: number;
  w?: number;
  h?: number;
}) => (
  <img
    src={BRACE_URL}
    alt=""
    style={{
      position: "absolute",
      left: pt(x),
      top: pt(y),
      width: pt(w),
      height: pt(h),
    }}
  />
);

/**
 * A fill-in field: an <input> for the browser, plus a plain span carrying the
 * same text for the capture.
 *
 * The span is not belt-and-braces. html2canvas does not use the browser's
 * layout for input values — it draws them itself, from the top of the element
 * box, ignoring line-height and padding, which slices the ascenders off every
 * value in the generated PDF while the browser shows them perfectly placed. A
 * span is ordinary text, which html2canvas does render correctly, so
 * `.is-capturing` hides the input's own text and shows this instead. The SS-4
 * and 2848 replicas have had this; the W-7 was still shipping clipped values.
 *
 * `base` is the baseline the value sits on, so entered text lines up with the
 * printed labels instead of floating in a box.
 */
export const Field = ({
  x,
  base,
  w,
  h = 10,
  size = 8,
  ...rest
}: {
  x: number;
  base: number;
  w: number;
  h?: number;
  size?: number;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, "size">) => {
  const lead = size * LINE_FACTOR;
  const top = base - baselineOffset(faceMetrics(W7_FACES.roman), size, lead);
  return (
    <>
      <input
        type="text"
        {...rest}
        style={{
          position: "absolute",
          left: pt(x),
          top: pt(base - h + 1),
          width: pt(w),
          height: pt(h),
          ...rest.style,
        }}
      />
      <span
        className="w7-print-value"
        style={{
          position: "absolute",
          left: pt(x),
          top: pt(top),
          width: pt(w),
          fontSize: pt(size),
          lineHeight: pt(lead),
          paddingLeft: pt(2),
        }}
      >
        {typeof rest.value === "string" || typeof rest.value === "number"
          ? rest.value
          : null}
      </span>
    </>
  );
};

/** Dotted write-in rules: a 2pt dash on a 3pt pitch, measured at 288dpi. */
const DOT_DASH = 2;
const DOT_PERIOD = 3;

/**
 * Draws every measured rule on the official form. Rendering the extracted
 * arrays directly means the replica's rule structure is the official's by
 * construction — there is nothing to keep in sync by hand.
 */
export const Rules = () => (
  <>
    {H_RULES.flatMap(([y, x0, x1, dotted], i) => {
      if (!dotted) {
        return [
          <div
            key={`h${i}`}
            style={{
              position: "absolute",
              left: pt(x0),
              top: pt(y - RULE_HALF),
              width: pt(x1 - x0),
              height: RULE_LEN,
              background: "#000",
            }}
          />,
        ];
      }
      // The six dotted write-in rules run a 2pt dash over a 1pt gap. They are
      // emitted as one element per dash, each drawn exactly like a solid rule:
      // html2canvas has no gradient support, so a repeating-linear-gradient
      // looks right in the browser and then disappears from the capture.
      const dashes = [];
      for (let x = x0; x < x1 - 0.01; x += DOT_PERIOD) {
        dashes.push(
          <div
            key={`h${i}-${dashes.length}`}
            style={{
              position: "absolute",
              left: pt(x),
              top: pt(y - RULE_HALF),
              width: pt(Math.min(DOT_DASH, x1 - x)),
              height: RULE_LEN,
              background: "#000",
            }}
          />,
        );
      }
      return dashes;
    })}
    {V_RULES.map(([x, y0, y1], i) => (
      <div
        key={`v${i}`}
        style={{
          position: "absolute",
          left: pt(x - RULE_HALF),
          top: pt(y0),
          height: pt(y1 - y0),
          width: RULE_LEN,
          background: "#000",
        }}
      />
    ))}
  </>
);

export { CHECKBOXES };
